import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useState } from 'react';
import type { PageItem } from '../lib/types';
import { PageCard } from './PageCard';

interface PageGridProps {
  pages: PageItem[];
  selected: Set<string>;
  splitAfter: Set<string>;
  onReorder: (activeUid: string, overUid: string) => void;
  onToggleSelect: (uid: string, additive: boolean, range: boolean) => void;
  onRotate: (uid: string, delta: number) => void;
  onRemove: (uid: string) => void;
  onToggleSplit: (uid: string) => void;
  onPreview: (uid: string) => void;
}

export function PageGrid({
  pages,
  selected,
  splitAfter,
  onReorder,
  onToggleSelect,
  onRotate,
  onRemove,
  onToggleSplit,
  onPreview,
}: PageGridProps) {
  const [dragging, setDragging] = useState<PageItem | null>(null);

  const sensors = useSensors(
    // A small distance threshold keeps clicks on the card's buttons from
    // being swallowed as drags.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragStart(event: DragStartEvent) {
    setDragging(pages.find((page) => page.uid === event.active.id) ?? null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setDragging(null);
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <SortableContext items={pages.map((page) => page.uid)} strategy={rectSortingStrategy}>
        <div className="page-grid">
          {pages.map((page, index) => (
            <PageCard
              key={page.uid}
              page={page}
              position={index + 1}
              selected={selected.has(page.uid)}
              splitAfter={splitAfter.has(page.uid) && index < pages.length - 1}
              onToggleSelect={onToggleSelect}
              onRotate={onRotate}
              onRemove={onRemove}
              onToggleSplit={onToggleSplit}
              onPreview={onPreview}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay>
        {dragging && (
          <div className="page-card" style={{ width: 168, opacity: 0.92 }}>
            <div className="page-card__thumb">
              <span className="page-card__placeholder">
                {selected.has(dragging.uid) && selected.size > 1
                  ? `Moving ${selected.size} pages`
                  : `Page ${dragging.pageIndex + 1}`}
              </span>
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
